import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { CourtLines } from '@/components/CourtCanvas';
import PlayExportFrames from '@/components/PlayExportFrames';
import PlayAnimation from '@/components/PlayAnimation';
import PlayTemplatesModal from '@/components/PlayTemplatesModal';
import useDrawing from '@/hooks/useDrawing';
import { exportPlayAsPDF, exportPlayAsPNG, shareViaWhatsApp } from '@/lib/exportPlay';
import { normalizePath, SPEED_PRESETS, speedPresetFor, DELAY_PRESETS, delayPresetFor } from '@/lib/playPaths';
import { api } from '@/lib/api';
import { writeWithQueue } from '@/lib/offlineSync';
import { TID } from '@/constants/testIds';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';
import SendToPlaysHubModal from '@/components/SendToPlaysHubModal';
import {
  Save, Plus, Trash2, MousePointer, Move as MoveIcon, Users,
  PenTool, Eraser, UserCircle, Hand, Square as SquareIcon,
  FileDown, Image as ImageIcon, Share2, Play, X as XIcon, Sparkles, Maximize2,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';

const GOLD = '#D4AF37';
const RED = '#E11D48';

const FORMATIONS = {
  default: [
    { id: 'o1', label: '1', team: 'offense', x: 50, y: 15 },
    { id: 'o2', label: '2', team: 'offense', x: 82, y: 45 },
    { id: 'o3', label: '3', team: 'offense', x: 18, y: 45 },
    { id: 'o4', label: '4', team: 'offense', x: 72, y: 78 },
    { id: 'o5', label: '5', team: 'offense', x: 28, y: 78 },
    { id: 'd1', label: 'X1', team: 'defense', x: 50, y: 25 },
    { id: 'd2', label: 'X2', team: 'defense', x: 80, y: 50 },
    { id: 'd3', label: 'X3', team: 'defense', x: 20, y: 50 },
    { id: 'd4', label: 'X4', team: 'defense', x: 68, y: 80 },
    { id: 'd5', label: 'X5', team: 'defense', x: 32, y: 80 },
  ],
  blank: [
    { id: 'b1', label: '1', team: 'offense', x: 50, y: 22 },
    { id: 'b2', label: '2', team: 'offense', x: 25, y: 40 },
    { id: 'b3', label: '3', team: 'offense', x: 75, y: 40 },
    { id: 'b4', label: '4', team: 'offense', x: 30, y: 65 },
    { id: 'b5', label: '5', team: 'offense', x: 70, y: 65 },
  ],
};

function buildStep(num, formation = 'default') {
  return {
    step_number: num,
    description: `Step ${num}`,
    players: FORMATIONS[formation].map(p => ({
      id: `${p.id}-${num}`,
      label: p.label,
      team: p.team,
      position: { x: p.x, y: p.y },
      photo_url: null,
      player_id: null,
      player_name: null,
    })),
    arrows: [],
    strokes: [], // freehand
  };
}

const TOOL_LABEL_KEYS = {
  select: 'move_players', move: 'arrow_move', pass: 'pass_tool',
  dribble: 'dribble_tool', screen: 'screen_tool', freehand: 'pencil_free',
  addOff: 'add_offense', addDef: 'add_defense', eraser: 'eraser_tool',
};

const TOOLS = [
  { id: 'select', icon: Hand },
  { id: 'move', icon: MoveIcon },
  { id: 'pass', icon: null },
  { id: 'dribble', icon: null },
  { id: 'screen', icon: null },
  { id: 'freehand', icon: PenTool },
  { id: 'addOff', icon: UserCircle },
  { id: 'addDef', icon: UserCircle },
  { id: 'eraser', icon: Eraser },
];

export default function PlayEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isBlank = searchParams.get('blank') === '1';
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [showPlaysHub, setShowPlaysHub] = useState(false);
  const [name, setName] = useState(isBlank ? '' : '');
  const [category, setCategory] = useState('man');
  const [difficulty, setDifficulty] = useState('intermediate');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([buildStep(1, isBlank ? 'blank' : 'default')]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [tool, setTool] = useState('select');

  const svgRef = useRef(null);
  const [roster, setRoster] = useState([]);
  const exportRefs = useRef([]);
  const [exporting, setExporting] = useState(null);
  // Presentation mode: full-screen, court FROZEN (no scroll, no pan), all
  // primary tool buttons visible. Used by the coach during games/time-outs
  // to show the play to players without accidental movement.
  const [presentMode, setPresentMode] = useState(false);
  // When inside the EXPAND overlay, the right sidebar (Mi Roster) is hidden by
  // CSS. The coach can still access it via a floating popover toggled by the
  // "Roster" button in the tactical toolbar.
  const [rosterPopover, setRosterPopover] = useState(false);
  // Inline animation in EXPAND mode: when true, the editor canvas is replaced
  // by <PlayAnimation> so the coach sees the play animate over the same court
  // without an extra modal. The tactical buttons collapse to a STOP control.
  const [inlineAnim, setInlineAnim] = useState(false);

  // While the overlay is open, lock the body scroll so accidental finger
  // gestures on the court don't scroll the page underneath. Cleanup restores
  // the original overflow when the overlay closes.
  useEffect(() => {
    if (!presentMode) return;
    const prev = document.body.style.overflow;
    const prevTouch = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = prev;
      document.body.style.touchAction = prevTouch;
    };
  }, [presentMode]);
  const [displayMode, setDisplayMode] = useState('photo'); // 'photo' | 'number' | 'name'
  const [previewOpen, setPreviewOpen] = useState(false);
  const [rosterDrawerOpen, setRosterDrawerOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [loadedSuccessRate, setLoadedSuccessRate] = useState(null);

  const playForExport = { id, name, category, difficulty, description, steps, success_rate: loadedSuccessRate != null ? loadedSuccessRate : 0.6 };

  const handleExportPDF = async () => {
    if (!exportRefs.current?.length) return toast.error(t('frames_not_ready'));
    setExporting('pdf');
    try {
      await exportPlayAsPDF(playForExport, exportRefs.current, {});
      toast.success(t('pdf_generated'));
    } catch (e) { console.error(e); toast.error(t('pdf_export_error')); }
    finally { setExporting(null); }
  };
  const handleExportPNG = async () => {
    if (!exportRefs.current?.length) return toast.error(t('frames_not_ready'));
    setExporting('png');
    try {
      await exportPlayAsPNG(playForExport, exportRefs.current);
      toast.success(t('image_downloaded'));
    } catch (e) { console.error(e); toast.error(t('image_export_error')); }
    finally { setExporting(null); }
  };
  const handleShareWhatsApp = async () => {
    if (!exportRefs.current?.length) return toast.error(t('frames_not_ready'));
    setExporting('wa');
    try {
      const blob = await exportPlayAsPNG(playForExport, exportRefs.current);
      const filename = `${(name || 'play').replace(/[^\w]+/g, '_')}.png`;
      const shared = await shareViaWhatsApp(playForExport, blob, filename);
      if (!shared) toast.info(t('whatsapp_open_attach'));
    } catch (e) { console.error(e); toast.error(t('share_error')); }
    finally { setExporting(null); }
  };

  useEffect(() => {
    api.get('/roster').then(r => setRoster(r.data.players || [])).catch(() => {});
    if (id) {
      api.get(`/plays/${id}`).then(r => {
        const p = r.data;
        setName(p.name);
        setCategory(p.category);
        setDifficulty(p.difficulty);
        setDescription(p.description || '');
        setSteps(p.steps?.length ? p.steps.map(s => ({ strokes: [], ...s })) : [buildStep(1)]);
        if (p.success_rate != null) setLoadedSuccessRate(p.success_rate);
      });
    }
  }, [id]);

  const active = steps[activeIdx];

  // All drawing/pointer logic lives in the useDrawing hook
  const {
    dragId, arrowStart, strokePoints, assignTargetId, setAssignTargetId,
    onPlayerPointerDown, onCanvasPointerDown, onCanvasPointerMove, onCanvasPointerUp,
    cancel: cancelDrawing,
  } = useDrawing({
    steps, setSteps, activeIdx, tool, svgRef,
    addPlayer: (team, pos) => addPlayer(team, pos),
    removePlayer: (pid) => removePlayer(pid),
  });

  // ===== Players CRUD =====
  const addPlayer = (team, pos) => {
    const teamPlayers = active.players.filter(p => p.team === team);
    const nextNum = teamPlayers.length + 1;
    const newP = {
      id: `${team[0]}${nextNum}-${active.step_number}-${Date.now()}`,
      label: team === 'offense' ? String(nextNum) : `X${nextNum}`,
      team,
      position: pos,
      photo_url: null,
      player_id: null,
      player_name: null,
    };
    setSteps(prev => prev.map((st, i) => i !== activeIdx ? st : ({ ...st, players: [...st.players, newP] })));
  };

  const removePlayer = (pid) => {
    setSteps(prev => prev.map((st, i) => i !== activeIdx ? st : ({ ...st, players: st.players.filter(p => p.id !== pid) })));
  };

  const removeStroke = (sid) => {
    setSteps(prev => prev.map((st, i) => i !== activeIdx ? st : ({ ...st, strokes: (st.strokes || []).filter(s => s.id !== sid) })));
  };

  // Quick-add a roster player onto the court at an unoccupied formation slot
  const quickAddRosterPlayer = (rp) => {
    const already = active.players.find(p => p.player_id === rp.id);
    if (already) {
      toast.info(`${rp.name} ${t('roster_player_in_court')}`);
      return;
    }
    const slots = [
      { x: 50, y: 25 }, { x: 82, y: 50 }, { x: 18, y: 50 },
      { x: 72, y: 75 }, { x: 28, y: 75 },
    ];
    let pos = { x: 50, y: 35 };
    for (const s of slots) {
      const taken = active.players.some(p => p.team === 'offense'
        && Math.abs(p.position.x - s.x) < 8 && Math.abs(p.position.y - s.y) < 8);
      if (!taken) { pos = { x: s.x, y: s.y }; break; }
    }
    const newP = {
      id: `o-${rp.id}-${active.step_number}-${Date.now()}`,
      label: String(rp.number),
      team: 'offense',
      position: pos,
      photo_url: rp.photo_url || null,
      player_id: rp.id,
      player_name: rp.name,
    };
    setSteps(prev => prev.map((st, i) => i !== activeIdx ? st : ({ ...st, players: [...st.players, newP] })));
    toast.success(`${rp.name} ${t('roster_player_added_to_court')}`);
  };

  const assignRoster = (playerId, rp) => {
    setSteps(prev => prev.map((s, i) => i !== activeIdx ? s : ({
      ...s,
      players: s.players.map(pl => pl.id === playerId ? {
        ...pl,
        photo_url: rp?.photo_url || null,
        player_id: rp?.id || null,
        player_name: rp?.name || null,
        label: rp ? String(rp.number) : pl.label,
      } : pl),
    })));
    setAssignTargetId(null);
  };

  // ===== Steps =====
  const addStep = () => {
    const last = steps[steps.length - 1];
    setSteps([...steps, {
      step_number: steps.length + 1,
      description: `${t('step_default_desc')} ${steps.length + 1}`,
      players: JSON.parse(JSON.stringify(last.players)),
      arrows: [], strokes: [],
    }]);
    setActiveIdx(steps.length);
  };
  const removeStep = (idx) => {
    if (steps.length === 1) return;
    const next = steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 }));
    setSteps(next);
    setActiveIdx(Math.max(0, Math.min(activeIdx, next.length - 1)));
  };
  const updateStepDesc = (txt) => setSteps(prev => prev.map((s, i) => i === activeIdx ? { ...s, description: txt } : s));

  // "Limpiar" — reset the active step entirely (annotations + paths + back to starter formation)
  const clearArrows = () => setSteps(prev => prev.map((s, i) =>
    i === activeIdx ? buildStep(s.step_number, isBlank ? 'blank' : 'default') : s
  ));

  // Toggle defense: if defenders exist on this step → remove them; otherwise
  // drop 5 defenders in a man-to-man shadow formation. The button label and
  // icon swap accordingly so the user sees the action it will perform.
  const hasDefenders = active.players.some(p => p.team === 'defense');
  const toggleDefenders = () => {
    if (hasDefenders) {
      setSteps(prev => prev.map((st, idx) => idx !== activeIdx ? st : ({
        ...st,
        players: st.players.filter(p => p.team !== 'defense'),
        // Clean any paths attached to removed defenders so we don't leave orphans
        paths: Object.fromEntries(
          Object.entries(st.paths || {}).filter(([pid]) => {
            const owner = st.players.find(p => p.id === pid);
            return !owner || owner.team !== 'defense';
          }),
        ),
      })));
      toast.success(t('editor_defenders_removed'));
      return;
    }
    // Place defenders ~5-8% in front of each offensive player so they shadow them naturally
    const offense = active.players.filter(p => p.team === 'offense').slice(0, 5);
    const baseSlots = [
      { x: 50, y: 25 }, { x: 80, y: 50 }, { x: 20, y: 50 },
      { x: 68, y: 80 }, { x: 32, y: 80 },
    ];
    const newDefs = [];
    for (let i = 0; i < 5; i++) {
      const off = offense[i];
      const slot = baseSlots[i];
      // Shadow the offensive player if any, else use slot
      const pos = off
        ? { x: Math.max(5, Math.min(95, off.position.x)), y: Math.max(5, Math.min(95, off.position.y + 8)) }
        : slot;
      newDefs.push({
        id: `d-quick-${active.step_number}-${i}-${Date.now()}`,
        label: `X${i + 1}`,
        team: 'defense',
        position: pos,
        photo_url: null,
        player_id: null,
        player_name: null,
      });
    }
    setSteps(prev => prev.map((st, idx) => idx !== activeIdx ? st : ({ ...st, players: [...st.players, ...newDefs] })));
    toast.success(t('editor_defenders_added'));
  };

  // ===== Templates =====
  const applyTemplate = (tpl) => {
    setName(tpl.name);
    setCategory(tpl.category || 'man');
    setDifficulty(tpl.difficulty || 'intermediate');
    setDescription(tpl.description || '');
    setLoadedSuccessRate(tpl.success_rate ?? null);
    // Deep-clone so subsequent edits don't mutate the template module
    setSteps(JSON.parse(JSON.stringify(tpl.steps)));
    setActiveIdx(0);
    setTemplatesOpen(false);
    toast.success(`Template "${tpl.name}" ✓`);
  };

  // ===== Save =====
  const save = async () => {
    const payload = { name, category, difficulty, description, steps, works_against: ['man'] };
    try {
      if (id) {
        const r = await writeWithQueue({ method: 'put', path: `/plays/${id}`, data: payload, optimistic: { id, ...payload } });
        toast.success(r.offline ? t('offline_saved_locally') : t('saved_ok'));
      } else {
        const r = await writeWithQueue({ method: 'post', path: '/plays', data: payload });
        if (r.offline) {
          toast.success(t('offline_saved_locally'));
        } else {
          toast.success(t('saved_ok'));
          navigate(`/playbook/${r.data.id}`);
        }
      }
    } catch { toast.error(t('save_error_msg')); }
  };

  const V = 1000;

  return (
    <Layout>
      <div
        // Wrap everything so that in present mode we can flip the whole UI
        // into a fixed overlay covering the tablet screen, with the page
        // body locked from scrolling and touch-action: none on the court.
        className={presentMode ? 'fixed inset-0 z-[60] bg-navy overflow-hidden p-2 sm:p-4 select-none editor-present-overlay' : ''}
        style={presentMode ? { touchAction: 'none' } : undefined}
        data-testid={presentMode ? 'editor-present-overlay' : undefined}
        data-present-mode={presentMode ? 'true' : undefined}
      >
        {/* Close button now lives in the main top bar (toggles Expandir → Cerrar). */}
        {presentMode && rosterPopover && (
          <div className="fixed top-24 right-3 z-[61] bg-card-steel border-2 border-gold/60 rounded-lg p-3 w-72 max-h-[70vh] overflow-auto shadow-2xl" data-testid="editor-present-roster-popover">
            <div className="flex items-center justify-between mb-2">
              <div className="font-h uppercase text-sm text-gold">{t('roster_h1')}</div>
              <button onClick={() => setRosterPopover(false)} className="text-gray-400 hover:text-white"><XIcon className="w-4 h-4" /></button>
            </div>
            {!roster?.length ? (
              <p className="text-xs text-gray-400">{t('no_roster_yet_label')} <span className="text-gold">{t('nav_roster')}</span></p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {roster.map(rp => {
                  const onCourt = active.players.some(p => p.player_id === rp.id);
                  return (
                    <button
                      key={rp.id}
                      onClick={() => quickAddRosterPlayer(rp)}
                      disabled={onCourt}
                      data-testid={`present-quick-add-${rp.id}`}
                      className={`border rounded-md p-1.5 flex flex-col items-center gap-1 text-center transition ${onCourt ? 'border-steel opacity-50' : 'border-steel hover:border-gold active:scale-95'}`}
                    >
                      <div className="w-12 h-12 rounded-full border-2 border-gold/60 overflow-hidden bg-navy-deep">
                        {rp.photo_url ? <img src={rp.photo_url} alt={rp.name} className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full font-h text-gold text-lg">{rp.number}</div>}
                      </div>
                      <div className="text-[10px] text-white font-semibold">{rp.name.split(' ')[0]}</div>
                      <div className="text-[10px] text-gold font-mono">#{rp.number}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">
            {isBlank ? t('blank_board') : t('nav_editor')}
          </div>
          <h1 className="font-h uppercase text-3xl sm:text-4xl">{t('editor_title')}</h1>
          <p className="text-xs text-gray-400 mt-1">{t('editor_subtitle')}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!id && (
            <button
              onClick={() => navigate('/editor')}
              data-testid="editor-pro-back-to-easy"
              className="px-4 py-2.5 rounded-md border border-gold/30 text-gold/80 hover:text-gold hover:bg-gold/5 font-h uppercase tracking-wider text-xs"
              title="Volver al modo simple"
            >
              ← Modo simple
            </button>
          )}
          {!isBlank && (
            <button onClick={() => navigate('/editor?pro=1&blank=1')} className="px-4 py-2.5 rounded-md border border-gold/40 text-gold font-h uppercase tracking-wider text-sm hover:bg-gold/10">
              {t('blank_board')}
            </button>
          )}
          <button
            onClick={toggleDefenders}
            data-testid="editor-toggle-defenders-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md border font-h uppercase tracking-wider text-xs ${
              hasDefenders
                ? 'border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10'
                : 'border-red-500/50 text-red-400 hover:bg-red-500/10'
            }`}
            title={hasDefenders ? t('editor_remove_defenders_title') : t('editor_add_defenders_title')}
          >
            <UserCircle className="w-3.5 h-3.5" /> {hasDefenders ? t('editor_remove_defenders') : t('editor_add_defenders')}
          </button>
          <button
            onClick={() => setTemplatesOpen(true)}
            data-testid="editor-templates-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md border border-gold/40 text-gold font-h uppercase tracking-wider text-xs hover:bg-gold/10"
            title={t('load_template_title')}
          >
            <Sparkles className="w-3.5 h-3.5" /> {t('templates')}
          </button>
          <button
            onClick={() => setPreviewOpen(true)}
            data-testid="editor-preview-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md border border-gold/40 text-gold font-h uppercase tracking-wider text-xs hover:bg-gold/10"
            title={t('animation_preview')}
          >
            <Play className="w-3.5 h-3.5" /> {t('preview_label')}
          </button>
          <button
            onClick={() => setRosterDrawerOpen(true)}
            data-testid="editor-mobile-roster-btn"
            className="inline-flex lg:hidden items-center gap-1.5 px-3 py-2.5 rounded-md border border-gold/40 text-gold font-h uppercase tracking-wider text-xs hover:bg-gold/10"
            title={t('quick_player_selection')}
          >
            <Users className="w-3.5 h-3.5" /> {t('nav_roster')}
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting !== null}
            data-testid="editor-export-pdf-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md border border-gold/40 text-gold font-h uppercase tracking-wider text-xs hover:bg-gold/10 disabled:opacity-50"
            title={t('export_pdf_title')}
          >
            <FileDown className="w-3.5 h-3.5" /> {exporting === 'pdf' ? '...' : 'PDF'}
          </button>
          <button
            onClick={handleExportPNG}
            disabled={exporting !== null}
            data-testid="editor-export-png-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md border border-gold/40 text-gold font-h uppercase tracking-wider text-xs hover:bg-gold/10 disabled:opacity-50"
            title={t('export_png_title')}
          >
            <ImageIcon className="w-3.5 h-3.5" /> {exporting === 'png' ? '...' : 'PNG'}
          </button>
          <button
            onClick={handleShareWhatsApp}
            disabled={exporting !== null}
            data-testid="editor-share-whatsapp-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-[#25D366] text-white font-h uppercase tracking-wider text-xs hover:brightness-110 disabled:opacity-50"
            title={t('share_whatsapp_title')}
          >
            <Share2 className="w-3.5 h-3.5" /> {exporting === 'wa' ? '...' : 'WhatsApp'}
          </button>
          {isAdmin && (
            <button
              onClick={() => {
                if (!exportRefs.current?.length) { toast.error(t('frames_not_ready')); return; }
                setShowPlaysHub(true);
              }}
              data-testid="editor-send-playshub-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-purple-500 text-white font-h uppercase tracking-wider text-xs hover:bg-purple-600"
              title="Enviar al Plays Hub público (admin)"
            >
              <Globe className="w-3.5 h-3.5" /> Plays Hub
            </button>
          )}
          <button
            onClick={() => setPresentMode(p => !p)}
            data-testid="editor-present-mode-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md border font-h uppercase tracking-wider text-xs ${
              presentMode
                ? 'border-red-500 text-red-400 hover:bg-red-500/10'
                : 'border-gold/40 text-gold hover:bg-gold/10'
            }`}
            title={presentMode ? t('present_mode_exit') : t('present_mode_title')}
          >
            {presentMode ? <XIcon className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            {presentMode ? t('present_mode_exit') : t('present_mode')}
          </button>
          <button data-testid={TID.editorSave} onClick={save}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light transition">
            <Save className="w-4 h-4" /> {t('save')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Court canvas — on tablets we want it filling most of the screen.
            On lg+ (≥1024px iPad landscape) keep the 3/1 split. On md (iPad
            portrait) it takes full width and the toolbox stacks below. */}
        <div className="lg:col-span-3">
          {presentMode && inlineAnim ? (
            <div data-testid="editor-inline-anim">
              <PlayAnimation
                play={{ id, name, category, difficulty, description, steps, original_lang: 'es' }}
                rosterMap={roster.reduce((m, r) => { m[r.id] = r; return m; }, {})}
                autoplay
              />
            </div>
          ) : (
          <div className="relative rounded-lg overflow-hidden border-2 border-gold/60 bg-navy-deep">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${V} ${V}`}
              className="w-full h-auto block touch-none select-none"
              preserveAspectRatio="xMidYMid meet"
              onPointerDown={onCanvasPointerDown}
              onPointerMove={onCanvasPointerMove}
              onPointerUp={onCanvasPointerUp}
              onPointerCancel={cancelDrawing}
              style={{ touchAction: 'none', background: 'radial-gradient(ellipse at center top, #0E2440, #0A192F 70%)' }}
            >
              {/* Court (shared with PlayAnimation) */}
              <CourtLines />

              {/* Player movement paths — drawn so the coach sees the recorded routes */}
              {Object.entries(active.paths || {}).map(([pid, raw]) => {
                const np = normalizePath(raw);
                if (!np) return null;
                const pts = np.points;
                return (
                <g key={`path-${pid}`} pointerEvents={tool === 'eraser' ? 'auto' : 'none'}
                  onPointerDown={(e) => {
                    if (tool === 'eraser') {
                      e.stopPropagation();
                      setSteps(prev => prev.map((st, i) => i !== activeIdx ? st : ({
                        ...st,
                        paths: Object.fromEntries(Object.entries(st.paths || {}).filter(([k]) => k !== pid)),
                      })));
                    }
                  }}
                  style={{ cursor: tool === 'eraser' ? 'pointer' : 'default' }}
                >
                  <polyline
                    points={pts.map(p => `${(p.x / 100) * V},${(p.y / 100) * V}`).join(' ')}
                    fill="none" stroke="#D4AF37" strokeWidth="5"
                    strokeLinecap="round" strokeLinejoin="round"
                    strokeDasharray="2 10" opacity="0.85"
                  />
                  {/* Endpoint dot */}
                  <circle cx={(pts[pts.length - 1].x / 100) * V} cy={(pts[pts.length - 1].y / 100) * V}
                    r="10" fill="#D4AF37" opacity="0.6" />
                </g>
                );
              })}

              {/* Freehand strokes (existing) */}
              {(active.strokes || []).map(s => (
                <g key={s.id} style={{ cursor: tool === 'eraser' ? 'pointer' : 'default' }} onPointerDown={(e) => { if (tool === 'eraser') { e.stopPropagation(); removeStroke(s.id); } }}>
                  <polyline
                    points={s.points.map(p => `${(p.x / 100) * V},${(p.y / 100) * V}`).join(' ')}
                    fill="none" stroke={s.color} strokeWidth={s.width || 6} strokeLinecap="round" strokeLinejoin="round"
                    style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}
                  />
                </g>
              ))}

              {/* Current stroke preview */}
              {strokePoints && strokePoints.length > 1 && (
                <polyline
                  points={strokePoints.map(p => `${(p.x / 100) * V},${(p.y / 100) * V}`).join(' ')}
                  fill="none" stroke={GOLD} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"
                />
              )}

              {/* Arrows */}
              {(active.arrows || []).map((a, i) => {
                const x1 = (a.from.x / 100) * V, y1 = (a.from.y / 100) * V, x2 = (a.to.x / 100) * V, y2 = (a.to.y / 100) * V;
                const color = a.type === 'pass' ? '#60A5FA' : a.type === 'dribble' ? RED : a.type === 'screen' ? '#10B981' : GOLD;
                const dash = a.type === 'pass' ? '12 8' : a.type === 'dribble' ? '4 6' : 'none';
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={dash} />;
              })}

              {/* Player tokens */}
              {active.players.map(p => {
                const x = (p.position.x / 100) * V, y = (p.position.y / 100) * V;
                const stroke = p.team === 'offense' ? GOLD : RED;
                const showPhoto = displayMode === 'photo' && p.photo_url;
                const initials = (p.player_name || '').trim().split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase() || '').join('');
                const showName = (displayMode === 'photo' || displayMode === 'name') && p.player_name;
                let labelText = p.label;
                if (displayMode === 'name' && initials) labelText = initials;
                return (
                  <g
                    key={p.id}
                    transform={`translate(${x},${y})`}
                    style={{ cursor: tool === 'select' ? 'grab' : tool === 'eraser' ? 'pointer' : 'crosshair' }}
                    onPointerDown={onPlayerPointerDown(p.id)}
                    onDoubleClick={() => setAssignTargetId(p.id)}
                  >
                    <circle r="42" fill="#0A192F" stroke={stroke} strokeWidth="3.5" />
                    {showPhoto ? (
                      <>
                        <defs><clipPath id={`eclip-${p.id}`}><circle r="38" /></clipPath></defs>
                        <image href={p.photo_url} x="-38" y="-38" width="76" height="76" clipPath={`url(#eclip-${p.id})`} preserveAspectRatio="xMidYMid slice" />
                        {/* number badge on photo */}
                        <circle cx="32" cy="-28" r="14" fill={stroke} />
                        <text x="32" y="-22" textAnchor="middle" fontFamily="Oswald" fontWeight="700" fontSize="18" fill="#0A192F">{p.label}</text>
                      </>
                    ) : (
                      <text textAnchor="middle" dy={displayMode === 'name' && initials ? '8' : '11'}
                        fontFamily="Oswald" fontWeight="700"
                        fontSize={displayMode === 'name' && initials ? '28' : '34'} fill={stroke}>
                        {labelText}
                      </text>
                    )}
                    {showName && p.player_name && (
                      <g>
                        <rect x="-58" y="48" width="116" height="22" rx="4" fill="#0A192F" stroke={stroke} strokeWidth="1.5" />
                        <text x="0" y="64" textAnchor="middle" fontFamily="IBM Plex Sans" fontSize="14" fontWeight="600" fill="#fff">
                          {p.player_name.length > 16 ? p.player_name.slice(0, 14) + '…' : p.player_name}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Floating toolbar — hidden in present mode; replaced by the
                large bottom tactical bar rendered below the court. */}
            {!presentMode && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-navy-deep/95 backdrop-blur border border-gold/60 rounded-full px-2 py-1.5 flex flex-wrap gap-1 max-w-[calc(100%-2rem)] justify-center">
              {TOOLS.map(b => {
                const label = t(TOOL_LABEL_KEYS[b.id]);
                return (
                <button
                  key={b.id}
                  onClick={() => setTool(b.id)}
                  data-testid={`editor-tool-${b.id}`}
                  title={label}
                  className={`px-2.5 py-1.5 rounded-full text-[11px] font-h uppercase tracking-wider transition inline-flex items-center gap-1 ${tool === b.id ? 'bg-gold text-navy' : 'text-gold hover:bg-gold/10'}`}
                >
                  {b.icon && <b.icon className="w-3 h-3" />}
                  <span className="hidden sm:inline">{label}</span>
                </button>
              );})}
              <button onClick={clearArrows} className="px-2.5 py-1.5 rounded-full text-[11px] font-h uppercase tracking-wider text-alert hover:bg-alert/10">
                {t('clear_text')}
              </button>
              {presentMode && (
                <button
                  onClick={() => {
                    // Scroll the roster card into view inside the overlay so the coach
                    // can drag a player. We surface the sidebar temporarily as a popover.
                    setRosterPopover(p => !p);
                  }}
                  data-testid="editor-present-roster-btn"
                  className="px-2.5 py-1.5 rounded-full text-[11px] font-h uppercase tracking-wider bg-gold/15 text-gold hover:bg-gold/25 inline-flex items-center gap-1"
                >
                  <Users className="w-3 h-3" /> {t('roster_h1')}
                </button>
              )}
            </div>
            )}

            {/* Display mode toggle — hidden in present mode to keep the
                screen minimal. */}
            {!presentMode && (
            <div className="absolute top-3 right-3 bg-navy-deep/90 border border-gold/40 rounded-md p-1 flex gap-1">
              {[
                { id: 'photo', label: t('photo') },
                { id: 'number', label: t('number') },
                { id: 'name', label: t('name') },
              ].map(m => (
                <button
                  key={m.id}
                  onClick={() => setDisplayMode(m.id)}
                  data-testid={`editor-display-${m.id}`}
                  className={`px-2.5 py-1 rounded text-[10px] font-h uppercase tracking-wider transition ${
                    displayMode === m.id ? 'bg-gold text-navy' : 'text-gold hover:bg-gold/10'
                  }`}
                >{m.label}</button>
              ))}
            </div>
            )}
          </div>
          )}

          {/* Assign roster modal */}
          {assignTargetId && (
            <div className="mt-3 bg-card-steel border-2 border-gold rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-h uppercase text-gold">{t('assign_player_circle')}</h3>
                <button onClick={() => setAssignTargetId(null)} className="text-gold hover:text-alert">✕</button>
              </div>
              <p className="text-xs text-gray-400 mb-3">{t('double_click_assign')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                <button onClick={() => assignRoster(assignTargetId, null)} className="border border-steel rounded p-2 text-xs hover:border-gold text-gray-300">
                  {t('unassigned')}
                </button>
                {roster.map(rp => (
                  <button
                    key={rp.id}
                    onClick={() => assignRoster(assignTargetId, rp)}
                    data-testid={`assign-${rp.id}`}
                    className="border border-steel rounded p-2 hover:border-gold flex flex-col items-center gap-1 text-center transition"
                  >
                    <div className="w-12 h-12 rounded-full border border-gold/50 overflow-hidden bg-navy-deep">
                      {rp.photo_url ? <img src={rp.photo_url} className="w-full h-full object-cover" alt={rp.name} />
                        : <div className="text-gold text-lg flex items-center justify-center h-full">{rp.number}</div>}
                    </div>
                    <div className="text-[11px] text-white leading-tight">{rp.name.split(' ')[0]}</div>
                    <div className="text-[10px] text-gold">#{rp.number}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-card-steel border border-gold/30 rounded-lg p-4 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('play_name')}</label>
              <input data-testid={TID.editorPlayName} value={name} onChange={e => setName(e.target.value)} placeholder={isBlank ? t('blank_board') : t('my_new_play')} className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-3 py-2 text-white outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('category')}</label>
              <select data-testid={TID.editorCategory} value={category} onChange={e => setCategory(e.target.value)} className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-3 py-2 text-white outline-none">
                <option value="man">{t('cat_man')}</option><option value="zone">{t('cat_zone')}</option>
                <option value="ato">{t('cat_ato')}</option><option value="blob">{t('cat_blob')}</option>
                <option value="slob">{t('cat_slob')}</option><option value="endgame">{t('cat_endgame')}</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('difficulty')}</label>
              <select value={difficulty} onChange={e => setDifficulty(e.target.value)} className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-3 py-2 text-white outline-none">
                <option value="beginner">{t('beginner')}</option><option value="intermediate">{t('intermediate')}</option><option value="advanced">{t('advanced')}</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('play_description')}</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
                className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-3 py-2 text-white outline-none resize-none" />
            </div>
          </div>

          <div className="bg-card-steel border border-gold/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-h uppercase text-sm text-gold">{t('steps_title')}</div>
              <button data-testid={TID.editorAddStep} onClick={addStep}
                className="text-xs px-2.5 py-1 rounded border border-gold/40 text-gold hover:bg-gold/10 transition">
                <Plus className="w-3 h-3 inline" /> {t('add_step')}
              </button>
            </div>
            <div className="space-y-1.5">
              {steps.map((s, i) => (
                <div key={i} className={`flex items-center gap-2 p-2 rounded border ${i === activeIdx ? 'border-gold bg-gold/10' : 'border-steel'}`}>
                  <button onClick={() => setActiveIdx(i)} className="flex-1 text-left text-sm">
                    <span className="font-mono text-gold me-2">{s.step_number}</span>
                    <span className="text-white">{s.description}</span>
                  </button>
                  {steps.length > 1 && (
                    <button onClick={() => removeStep(i)} className="text-alert"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('step_description')}</label>
              <input value={active.description} onChange={e => updateStepDesc(e.target.value)}
                className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-3 py-2 text-white outline-none text-sm" />
            </div>
          </div>


          {/* Path timing — speed & delay per route */}
          {Object.keys(active.paths || {}).length > 0 && (
            <div className="bg-card-steel border border-gold/30 rounded-lg p-4">
              <div className="font-h uppercase text-sm text-gold mb-1 flex items-center gap-1">
                <PenTool className="w-3.5 h-3.5" /> {t('route_timing')}
              </div>
              <p className="text-[11px] text-gray-400 mb-3 leading-relaxed">
                {t('route_timing_desc')}
              </p>
              <div className="space-y-4">
                {Object.entries(active.paths).map(([pid, raw]) => {
                  const np = normalizePath(raw); if (!np) return null;
                  const player = active.players.find(p => p.id === pid);
                  const label = player?.player_name || `#${player?.label || pid}`;
                  const sPreset = speedPresetFor(np.speed);
                  const dPreset = delayPresetFor(np.delay);
                  const updatePath = (changes) => {
                    setSteps(prev => prev.map((st, i) => i !== activeIdx ? st : ({
                      ...st,
                      paths: { ...(st.paths || {}), [pid]: { points: np.points, speed: np.speed, delay: np.delay, ...changes } },
                    })));
                  };
                  return (
                    <div key={pid} data-testid={`speed-row-${pid}`} className="border-t border-steel pt-3 first:border-t-0 first:pt-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-white font-semibold">{label}</span>
                        <span className="text-[10px] text-gold font-mono">{sPreset.short} · ⏱ {dPreset.short}</span>
                      </div>
                      <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">{t('speed')}</div>
                      <div className="grid grid-cols-4 gap-1 mb-2">
                        {SPEED_PRESETS.map(sp => (
                          <button
                            key={sp.id}
                            onClick={() => updatePath({ speed: sp.value })}
                            data-testid={`speed-${pid}-${sp.id}`}
                            className={`text-[10px] font-h uppercase tracking-wider py-1.5 rounded transition ${
                              sPreset.id === sp.id ? 'bg-gold text-navy' : 'border border-gold/30 text-gold hover:bg-gold/10'
                            }`}
                            title={`${sp.label} (${sp.short})`}
                          >
                            {sp.emoji ? `${sp.emoji} ` : ''}{sp.short}
                          </button>
                        ))}
                      </div>
                      <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">{t('delay_exit')}</div>
                      <div className="grid grid-cols-4 gap-1">
                        {DELAY_PRESETS.map(dp => (
                          <button
                            key={dp.id}
                            onClick={() => updatePath({ delay: dp.value })}
                            data-testid={`delay-${pid}-${dp.id}`}
                            className={`text-[10px] font-h uppercase tracking-wider py-1.5 rounded transition ${
                              dPreset.id === dp.id ? 'bg-gold text-navy' : 'border border-gold/30 text-gold hover:bg-gold/10'
                            }`}
                            title={`${dp.label} (${dp.short})`}
                          >
                            {dp.short}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-card-steel border border-gold/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-h uppercase text-sm text-gold flex items-center gap-1">
                <Users className="w-3.5 h-3.5" /> {t('my_roster_quick')}
              </div>
              <span className="text-[10px] text-gray-400">{roster.length} {t('players_count')}</span>
            </div>
            {roster.length === 0 ? (
              <p className="text-xs text-gray-400">
                {t('no_roster_yet_label')} <span className="text-gold">{t('nav_roster')}</span> {t('no_roster_yet_label_after')}
              </p>
            ) : (
              <>
                <p className="text-[11px] text-gray-400 mb-2 leading-relaxed">
                  {t('quick_add_hint')}
                </p>
                <div className="grid grid-cols-3 gap-2 max-h-[320px] overflow-y-auto pr-1">
                  {roster.map(rp => {
                    const onCourt = active.players.some(p => p.player_id === rp.id);
                    return (
                      <button
                        key={rp.id}
                        onClick={() => quickAddRosterPlayer(rp)}
                        disabled={onCourt}
                        data-testid={`quick-add-${rp.id}`}
                        className={`relative border rounded-md p-1.5 flex flex-col items-center gap-1 text-center transition ${
                          onCourt
                            ? 'border-steel opacity-50 cursor-not-allowed'
                            : 'border-steel hover:border-gold hover:bg-gold/5 active:scale-95'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-full border-2 border-gold/60 overflow-hidden bg-navy-deep">
                          {rp.photo_url ? (
                            <img src={rp.photo_url} alt={rp.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="flex items-center justify-center h-full font-h text-gold text-lg">{rp.number}</div>
                          )}
                        </div>
                        <div className="text-[10px] text-white leading-tight font-semibold">{rp.name.split(' ')[0]}</div>
                        <div className="text-[10px] text-gold font-mono">#{rp.number}{rp.position ? ` · ${rp.position}` : ''}</div>
                        {onCourt && (
                          <div className="absolute top-1 right-1 bg-gold text-navy text-[8px] font-h px-1 rounded">{t('on_court_badge')}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="bg-card-steel border border-gold/30 rounded-lg p-4 text-xs">
            <div className="font-h uppercase text-sm text-gold mb-1 flex items-center gap-1">
              <PenTool className="w-3.5 h-3.5" /> {t('tips_label')}
            </div>
            <p className="text-gray-400 mb-1 leading-relaxed">
              <span className="text-gold">{t('add_offense')}</span> / <span className="text-gold">{t('add_defense')}</span>: {t('tip_offense_defense_a')}
            </p>
            <p className="text-gray-400 mb-1 leading-relaxed">
              <span className="text-gold">{t('pencil_free')}</span>: {t('tip_pencil_a')} <span className="text-gold">{t('tip_pencil_b')}</span> {t('tip_pencil_c')}
            </p>
            <p className="text-gray-400 leading-relaxed">
              {t('tip_double_tap')}
            </p>
          </div>
        </div>
      </div>

      {/* Floating STOP button — always visible during inline animation so
          the coach can quickly return to editing without scrolling. */}
      {presentMode && inlineAnim && (
        <button
          onClick={() => setInlineAnim(false)}
          data-testid="editor-present-tool-stop"
          className="fixed top-3 left-1/2 -translate-x-1/2 z-[62] px-6 py-3 rounded-full text-sm font-h uppercase tracking-wider bg-alert text-white hover:bg-alert/90 inline-flex items-center gap-2 font-semibold shadow-2xl"
        >
          <XIcon className="w-4 h-4" /> {t('stop_edit')}
        </button>
      )}

      {/* Present-mode tactical toolbar — appears BELOW the court, in the
          empty space, so the coach can reach the buttons without obscuring
          the play. Buttons are touch-optimised (min-h 56px). Hidden during
          inline animation since PlayAnimation has its own controls. */}
      {presentMode && !inlineAnim && (
        <div
          className="mt-4 flex flex-wrap items-center justify-center gap-2 px-3 py-3 rounded-2xl bg-navy-deep/95 border-2 border-gold/40 shadow-2xl"
          data-testid="editor-present-tactical-bar"
        >
          <button
            onClick={() => setInlineAnim(true)}
            data-testid="editor-present-tool-play"
            title={t('play')}
            disabled={!steps || steps.length === 0}
            className="px-4 py-3 rounded-lg text-sm font-h uppercase tracking-wider bg-gold text-navy hover:bg-gold-light inline-flex items-center gap-2 min-h-[56px] font-semibold shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Play className="w-4 h-4 fill-current" /> {t('play')}
          </button>
          {TOOLS.map(b => {
            const label = t(TOOL_LABEL_KEYS[b.id]);
            return (
              <button
                key={b.id}
                onClick={() => setTool(b.id)}
                data-testid={`editor-present-tool-${b.id}`}
                title={label}
                className={`px-4 py-3 rounded-lg text-sm font-h uppercase tracking-wider transition inline-flex items-center gap-2 min-h-[56px] min-w-[56px] ${
                  tool === b.id ? 'bg-gold text-navy shadow-lg' : 'border-2 border-gold/40 text-gold hover:bg-gold/10'
                }`}
              >
                {b.icon && <b.icon className="w-4 h-4" />}
                <span>{label}</span>
              </button>
            );
          })}
          <button
            onClick={clearArrows}
            data-testid="editor-present-tool-clear"
            className="px-4 py-3 rounded-lg text-sm font-h uppercase tracking-wider text-alert border-2 border-alert/40 hover:bg-alert/10 min-h-[56px]"
          >
            {t('clear_text')}
          </button>
          <button
            onClick={() => setRosterPopover(p => !p)}
            data-testid="editor-present-tool-roster"
            className={`px-4 py-3 rounded-lg text-sm font-h uppercase tracking-wider inline-flex items-center gap-2 min-h-[56px] ${
              rosterPopover ? 'bg-gold text-navy shadow-lg' : 'border-2 border-gold/40 text-gold hover:bg-gold/10'
            }`}
          >
            <Users className="w-4 h-4" /> {t('roster_h1')}
          </button>
        </div>
      )}

      {/* Offscreen frames for export */}
      <PlayExportFrames
        steps={steps}
        onReady={(refs) => { exportRefs.current = refs; }}
      />

      {/* Preview modal — animate the play steps without leaving the editor */}
      <PlayTemplatesModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onApply={applyTemplate}
      />
      {previewOpen && (
        <div
          className="fixed inset-0 z-50 bg-navy-deep/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
          data-testid="editor-preview-modal"
        >
          <div className="w-full max-w-3xl max-h-[95vh] overflow-y-auto bg-navy border-2 border-gold/60 rounded-lg p-4 sm:p-6 relative">
            <button
              onClick={() => setPreviewOpen(false)}
              data-testid="editor-preview-close"
              className="absolute top-3 right-3 p-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10"
              aria-label={t('close')}
            >
              <XIcon className="w-4 h-4" />
            </button>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">{t('preview_label')}</div>
            <h3 className="font-h uppercase text-2xl sm:text-3xl mb-4 pr-10">
              {name || t('no_name')}
            </h3>
            <PlayAnimation
              play={{ name, category, difficulty, description, steps }}
              rosterMap={roster.reduce((m, r) => { m[r.id] = r; return m; }, {})}
              autoplay
            />
          </div>
        </div>
      )}

      {/* Mobile roster drawer */}
      {rosterDrawerOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden bg-navy-deep/85 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setRosterDrawerOpen(false); }}
          data-testid="editor-roster-drawer"
        >
          <div className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-navy border-l-2 border-gold/60 p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="font-h uppercase text-sm text-gold flex items-center gap-1">
                <Users className="w-4 h-4" /> {t('my_roster')}
              </div>
              <button
                onClick={() => setRosterDrawerOpen(false)}
                data-testid="editor-roster-drawer-close"
                className="p-1.5 rounded border border-gold/40 text-gold hover:bg-gold/10"
                aria-label={t('close')}
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            {roster.length === 0 ? (
              <p className="text-xs text-gray-400">
                {t('no_roster_yet_label')} <span className="text-gold">{t('nav_roster')}</span>.
              </p>
            ) : (
              <>
                <p className="text-[11px] text-gray-400 mb-3 leading-relaxed">
                  {t('quick_add_hint')}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {roster.map(rp => {
                    const onCourt = active.players.some(p => p.player_id === rp.id);
                    return (
                      <button
                        key={rp.id}
                        onClick={() => { quickAddRosterPlayer(rp); }}
                        disabled={onCourt}
                        data-testid={`drawer-quick-add-${rp.id}`}
                        className={`relative border rounded-md p-1.5 flex flex-col items-center gap-1 text-center transition ${
                          onCourt ? 'border-steel opacity-50' : 'border-steel hover:border-gold active:scale-95'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-full border-2 border-gold/60 overflow-hidden bg-navy-deep">
                          {rp.photo_url ? (
                            <img src={rp.photo_url} alt={rp.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="flex items-center justify-center h-full font-h text-gold text-lg">{rp.number}</div>
                          )}
                        </div>
                        <div className="text-[10px] text-white leading-tight font-semibold">{rp.name.split(' ')[0]}</div>
                        <div className="text-[10px] text-gold font-mono">#{rp.number}</div>
                        {onCourt && (
                          <div className="absolute top-1 right-1 bg-gold text-navy text-[8px] font-h px-1 rounded">{t('on_court_badge')}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </div>

      {showPlaysHub && (
        <SendToPlaysHubModal
          play={{ id, name, category, description }}
          exportNode={exportRefs.current?.[0]}
          onClose={() => setShowPlaysHub(false)}
          onSent={() => { /* Already toasted by the modal. */ }}
        />
      )}
    </Layout>
  );
}
